/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailOrganizeFilters.js
 * @description
 * Collapsible "Organize" card for `MailHomeScreen` (every account): filter the list by colour
 * mark, AI category or label, with counts from `GET /mail/organize/summary`. Selecting the same
 * pill again clears the filter. Renders nothing while the summary has no colour / category /
 * label to offer. Sits next to (never replaces) the folder pills and the collaboration card.
 *
 * Filter shape reported to the parent: `{color?:string, category?:string, label?:string}` or `null`.
 */

import React, {useMemo, useState} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {FolderPill} from './MailUiKit';
import {useManagementTheme} from '../../helpers/managementTheme';
import {colorHex} from '../../helpers/mailOrganize';

/** Titled horizontal pill row. */
const PillRow = ({title, children}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.block}>
            <Text style={[styles.blockTitle, {color: m.colors.muted}]}>{title}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pills}>
                {children}
            </ScrollView>
        </View>
    );
};

/**
 * Organize filter card.
 * @param {Object} props
 * @param {{colors:Array, categories:Array, labels:Array}} props.summary Normalised organize summary.
 * @param {{color?:string, category?:string, label?:string}|null} props.filter Active filter.
 * @param {(next: Object|null) => void} props.onChange Filter handler.
 * @returns {JSX.Element|null}
 */
const MailOrganizeFilters = ({summary, filter, onChange}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [open, setOpen] = useState(Boolean(filter));
    const colors = useMemo(() => (Array.isArray(summary?.colors) ? summary.colors : []), [summary]);
    const categories = useMemo(() => (Array.isArray(summary?.categories) ? summary.categories : []), [summary]);
    const labels = useMemo(() => (Array.isArray(summary?.labels) ? summary.labels : []), [summary]);

    if (!colors.length && !categories.length && !labels.length && !filter) return null;

    const toggle = (field, value) => onChange(filter?.[field] === value ? null : {[field]: value});

    return (
        <View style={[styles.card, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            <TouchableOpacity onPress={() => setOpen((v) => !v)} activeOpacity={0.8} style={styles.head} accessibilityRole="button">
                <Icon name="palette-outline" size={17} color={m.colors.primary} />
                <Text style={[styles.headText, {color: m.colors.heading}]} numberOfLines={1}>{t('mail_organize_filters')}</Text>
                {!!filter && (
                    <TouchableOpacity onPress={() => onChange(null)} hitSlop={8} accessibilityLabel={t('mail_collab_clear')}>
                        <Icon name="close-circle" size={17} color={m.colors.muted} />
                    </TouchableOpacity>
                )}
                <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} color={m.colors.muted} />
            </TouchableOpacity>
            {open && (
                <View>
                    {colors.length > 0 && (
                        <PillRow title={t('mail_organize_color')}>
                            {colors.map((c) => (
                                <FolderPill
                                    key={`c-${c.key}`}
                                    label={t(`mail_color_${c.key}`, {defaultValue: String(c.key)})}
                                    icon="circle"
                                    color={colorHex(c.key, m.isDark)}
                                    count={c.unread || 0}
                                    active={filter?.color === c.key}
                                    onPress={() => toggle('color', c.key)}
                                />
                            ))}
                        </PillRow>
                    )}
                    {categories.length > 0 && (
                        <PillRow title={t('mail_organize_categories')}>
                            {categories.map((c) => (
                                <FolderPill
                                    key={`k-${c.key}`}
                                    label={t(`mail_ai_cat_${c.key}`, {defaultValue: String(c.key)})}
                                    icon="shape-outline"
                                    count={c.unread || 0}
                                    active={filter?.category === c.key}
                                    onPress={() => toggle('category', c.key)}
                                />
                            ))}
                        </PillRow>
                    )}
                    {labels.length > 0 && (
                        <PillRow title={t('mail_organize_labels')}>
                            {labels.map((l) => (
                                <FolderPill
                                    key={`l-${l.key}`}
                                    label={l.name || String(l.key)}
                                    icon="tag-outline"
                                    color={l.color || undefined}
                                    count={l.unread || 0}
                                    active={filter?.label === l.key}
                                    onPress={() => toggle('label', l.key)}
                                />
                            ))}
                        </PillRow>
                    )}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    card: {marginHorizontal: 16, marginTop: 10, borderRadius: 22, borderWidth: 1, paddingBottom: 4},
    head: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 11},
    headText: {flex: 1, fontSize: 14, fontWeight: '800'},
    block: {marginTop: 2},
    blockTitle: {fontSize: 10, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase', paddingHorizontal: 14, marginBottom: 4},
    pills: {gap: 8, paddingHorizontal: 12, paddingBottom: 8},
});

export default MailOrganizeFilters;
